/**
 * AdminCategoryGroups (iter82) — tenant-level Group Categories CRUD +
 * shop-product-category mapping editor.
 *
 * Workflow:
 *   1. Top half lists every tenant Group Category (image, name, mapped
 *      count). Inline edit + add + delete.
 *   2. Bottom half is the Mapping editor — each shop is a collapsible
 *      panel; inside each panel every shop ProductCategory has a
 *      dropdown of Group Categories so the admin can map it.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2, Pencil, Trash2, ChevronDown, ChevronRight, Save, Package } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ImageUploader from "../../components/ImageUploader";

const EMPTY_FORM = {
    name: "",
    slug: "",
    image_url: null,
    sort_order: 0,
    description: "",
    business_category_id: null,
};

export default function AdminCategoryGroups() {
    const [groups, setGroups] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [mappingData, setMappingData] = useState(null);
    const [pendingMappings, setPendingMappings] = useState({}); // { product_category_id: group_id | null }
    const [expandedShops, setExpandedShops] = useState({});
    const [modal, setModal] = useState(null);  // { mode: "create" | "edit", row? }
    const [form, setForm] = useState(EMPTY_FORM);
    const [savingForm, setSavingForm] = useState(false);
    const [savingMappings, setSavingMappings] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        try {
            const [g, m, v] = await Promise.all([
                api.get("/admin/category-groups"),
                api.get("/admin/category-groups/mappings"),
                api.get("/admin/business-categories"),
            ]);
            setGroups(g.data || []);
            setMappingData(m.data || { shops: [] });
            setVerticals((v.data || []).filter((x) => !x.parent_id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load category groups");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); }, []);

    const groupLookup = useMemo(() => {
        const m = {};
        for (const g of groups) m[g.id] = g;
        return m;
    }, [groups]);

    // ---------- group CRUD ----------
    const openCreate = () => { setForm(EMPTY_FORM); setModal({ mode: "create" }); };
    const openEdit = (row) => {
        setForm({
            name: row.name || "",
            slug: row.slug || "",
            image_url: row.image_url || null,
            sort_order: row.sort_order || 0,
            description: row.description || "",
            business_category_id: row.business_category_id || null,
        });
        setModal({ mode: "edit", row });
    };

    const saveGroup = async () => {
        if (!form.name?.trim()) return toast.error("Name is required");
        setSavingForm(true);
        try {
            if (modal.mode === "create") {
                await api.post("/admin/category-groups", form);
                toast.success("Group created");
            } else {
                await api.patch(`/admin/category-groups/${modal.row.id}`, form);
                toast.success("Group updated");
            }
            setModal(null);
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingForm(false);
        }
    };

    const deleteGroup = async (row) => {
        if (!window.confirm(`Delete "${row.name}"? Any shop categories mapped to this group will be unmapped (not deleted).`)) return;
        try {
            await api.delete(`/admin/category-groups/${row.id}`);
            toast.success("Group deleted");
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // ---------- mappings ----------
    const setMapping = (productCategoryId, groupId) => {
        setPendingMappings((m) => ({ ...m, [productCategoryId]: groupId || null }));
    };
    const effectiveMapping = (cat) => {
        if (pendingMappings[cat.id] !== undefined) return pendingMappings[cat.id];
        return cat.group_category_id || null;
    };
    const pendingCount = Object.keys(pendingMappings).length;

    const saveMappings = async () => {
        const items = Object.entries(pendingMappings).map(([pid, gid]) => ({
            product_category_id: pid,
            group_category_id: gid,
        }));
        if (items.length === 0) return;
        setSavingMappings(true);
        try {
            const { data } = await api.post("/admin/category-groups/mappings", { mappings: items });
            toast.success(`Saved ${data.updated} of ${data.total} mappings`);
            setPendingMappings({});
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingMappings(false);
        }
    };

    const toggleShop = (sid) => setExpandedShops((m) => ({ ...m, [sid]: !m[sid] }));
    const expandAll = () => {
        const all = {};
        (mappingData?.shops || []).forEach((s) => { all[s.id] = true; });
        setExpandedShops(all);
    };
    const collapseAll = () => setExpandedShops({});

    if (loading) return (
        <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={18} /> Loading category groups…</div>
    );

    return (
        <div data-testid="admin-category-groups" className="space-y-6">
            {/* ---------- Header ---------- */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold">Group Categories</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Roll up every shop&apos;s individual product categories into unified groups so customers can browse one
                        &quot;Biryanis&quot; bucket instead of N near-duplicate categories per shop.
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    data-testid="group-add"
                    className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                >
                    <Plus size={14} /> New Group
                </button>
            </div>

            {/* ---------- Group list ---------- */}
            <section className="bg-card border border-border rounded-2xl p-4" data-testid="groups-list">
                <h2 className="font-bold text-sm mb-3">All Groups ({groups.length})</h2>
                {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">No groups yet — click <strong>New Group</strong> to add one.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {groups.map((g) => (
                            <div
                                key={g.id}
                                data-testid={`group-row-${g.slug}`}
                                className="bg-muted/30 border border-border rounded-xl p-3 flex items-start gap-3"
                            >
                                {g.image_url ? (
                                    <img src={g.image_url} alt={g.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center flex-shrink-0">
                                        <Package size={20} />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm truncate">{g.name}</p>
                                    <p className="text-[11px] text-muted-foreground truncate">/{g.slug}</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                        {g.mapped_count} mapped · sort {g.sort_order}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <button
                                        onClick={() => openEdit(g)}
                                        data-testid={`group-edit-${g.slug}`}
                                        className="p-1.5 text-muted-foreground hover:text-primary"
                                        title="Edit"
                                    ><Pencil size={14} /></button>
                                    <button
                                        onClick={() => deleteGroup(g)}
                                        data-testid={`group-delete-${g.slug}`}
                                        className="p-1.5 text-muted-foreground hover:text-destructive"
                                        title="Delete"
                                    ><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* ---------- Mappings ---------- */}
            <section className="bg-card border border-border rounded-2xl p-4" data-testid="mappings-section">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div>
                        <h2 className="font-bold text-sm">Map Shop Categories to Groups</h2>
                        <p className="text-[11px] text-muted-foreground">Each shop&apos;s product categories get a group assignment. Save when done.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={expandAll} className="text-[11px] font-semibold text-primary">Expand all</button>
                        <span className="text-muted-foreground">·</span>
                        <button onClick={collapseAll} className="text-[11px] font-semibold text-primary">Collapse all</button>
                        <button
                            onClick={saveMappings}
                            disabled={pendingCount === 0 || savingMappings}
                            data-testid="mappings-save"
                            className="ml-2 rounded-xl bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {savingMappings ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            Save {pendingCount > 0 ? `(${pendingCount})` : ""}
                        </button>
                    </div>
                </div>

                {(mappingData?.shops || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">No shops yet — onboard restaurants first to map their product categories.</p>
                ) : (
                    <div className="space-y-2">
                        {mappingData.shops.map((s) => {
                            const expanded = !!expandedShops[s.id];
                            const mappedInShop = s.categories.filter(
                                (c) => effectiveMapping(c),
                            ).length;
                            return (
                                <div key={s.id} data-testid={`mapping-shop-${s.id}`} className="border border-border rounded-xl overflow-hidden">
                                    <button
                                        onClick={() => toggleShop(s.id)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 text-left"
                                    >
                                        {expanded ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronRight size={16} className="text-muted-foreground" />}
                                        {s.logo_url ? (
                                            <img src={s.logo_url} alt={s.name} className="w-8 h-8 rounded-lg object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-lg bg-muted" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm truncate">{s.name}</p>
                                            <p className="text-[11px] text-muted-foreground">
                                                {s.categories.length} cats · {mappedInShop} mapped
                                            </p>
                                        </div>
                                    </button>
                                    {expanded && (
                                        <div className="border-t border-border divide-y divide-border bg-background/50">
                                            {s.categories.length === 0 ? (
                                                <p className="p-3 text-xs text-muted-foreground">No product categories.</p>
                                            ) : s.categories.map((cat) => {
                                                const current = effectiveMapping(cat);
                                                const isPending = pendingMappings[cat.id] !== undefined && pendingMappings[cat.id] !== (cat.group_category_id || null);
                                                return (
                                                    <div
                                                        key={cat.id}
                                                        data-testid={`mapping-cat-${cat.id}`}
                                                        className={`px-3 py-2 flex items-center gap-3 ${isPending ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}
                                                    >
                                                        {cat.image_url ? (
                                                            <img src={cat.image_url} alt={cat.name} className="w-7 h-7 rounded object-cover" />
                                                        ) : (
                                                            <div className="w-7 h-7 rounded bg-muted" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{cat.name}</p>
                                                            {cat.status !== "ACTIVE" && (
                                                                <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">inactive</span>
                                                            )}
                                                        </div>
                                                        <select
                                                            data-testid={`mapping-select-${cat.id}`}
                                                            value={current || ""}
                                                            onChange={(e) => setMapping(cat.id, e.target.value || null)}
                                                            className="text-xs bg-card border border-border rounded-lg px-2 py-1.5 min-w-[160px]"
                                                        >
                                                            <option value="">— Unmapped —</option>
                                                            {groups.map((g) => (
                                                                <option key={g.id} value={g.id}>{g.name}</option>
                                                            ))}
                                                        </select>
                                                        {isPending && (
                                                            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">unsaved</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ---------- Modal ---------- */}
            {modal && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setModal(null)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md md:max-w-lg p-5 shadow-2xl max-h-[92vh] overflow-y-auto flex flex-col"
                        data-testid="group-modal"
                    >
                        <div className="flex items-center justify-between mb-4 shrink-0">
                            <h3 className="font-bold text-lg">{modal.mode === "create" ? "New Group Category" : "Edit Group Category"}</h3>
                            <button onClick={() => setModal(null)} className="text-muted-foreground"><X size={18} /></button>
                        </div>

                        <div className="space-y-4 flex-1 overflow-y-auto -mx-1 px-1">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Name *</label>
                                <input
                                    data-testid="group-name-input"
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="Biryanis"
                                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm mt-1 outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Slug</label>
                                <input
                                    data-testid="group-slug-input"
                                    type="text"
                                    value={form.slug}
                                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                                    placeholder="auto-generated from name"
                                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm mt-1 outline-none font-mono"
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">Used in the customer URL <code>/g/&#123;slug&#125;</code>. Auto-generated if blank.</p>
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Image</label>
                                <div className="mt-1">
                                    <ImageUploader
                                        value={form.image_url}
                                        onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))}
                                        testId="group-image-upload"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Sort order</label>
                                    <input
                                        data-testid="group-sort-input"
                                        type="number"
                                        value={form.sort_order}
                                        onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
                                        className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm mt-1 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Pin to vertical</label>
                                    <select
                                        data-testid="group-vertical-select"
                                        value={form.business_category_id || ""}
                                        onChange={(e) => setForm((f) => ({ ...f, business_category_id: e.target.value || null }))}
                                        className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm mt-1 outline-none"
                                    >
                                        <option value="">— All verticals —</option>
                                        {verticals.map((v) => (
                                            <option key={v.id} value={v.id}>{v.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Description</label>
                                <textarea
                                    data-testid="group-desc-input"
                                    value={form.description}
                                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                    rows={2}
                                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm mt-1 outline-none"
                                />
                            </div>
                        </div>

                        <div className="mt-5 pt-3 border-t border-border/60 flex items-center justify-end gap-2 shrink-0 sticky bottom-0 bg-card">
                            <button onClick={() => setModal(null)} data-testid="group-cancel" className="px-4 py-2 text-sm font-semibold text-muted-foreground">Cancel</button>
                            <button
                                onClick={saveGroup}
                                disabled={savingForm || !form.name.trim()}
                                data-testid="group-save"
                                className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {savingForm && <Loader2 size={14} className="animate-spin" />}
                                {modal.mode === "create" ? "Create" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
